import { EINHEIT_ART_LABEL, standardEinheitId } from '@uep/shared';
import { db } from './db';
import { inDieOutbox } from './outbox';
import type { LokaleEinheit } from './typen';

/**
 * Heilung der Einheitenebene (Objekt → Einheit → Raum).
 *
 * Die Einheit kam nach dem ersten Produktivstand dazu. Damit überall dieselbe
 * Kette gilt, bekommt jedes Objekt ohne Einheit automatisch eine
 * Standardeinheit, und Räume ohne Einheitenbezug wandern dort hinein.
 *
 * Die ID der Standardeinheit wird aus der Objekt-ID abgeleitet
 * (`<objektId>--einheit`). Sie ist damit auf jedem Gerät dieselbe — sonst legte
 * jedes Tablet, das ein Altobjekt zum ersten Mal sieht, eine eigene Einheit an.
 *
 * Diese Datei greift bewusst nur auf `db` und die Outbox zu, nicht auf den
 * Sync-Worker: Sie wird aus `sync.ts` und `pull.ts` aufgerufen und darf keinen
 * Importzyklus erzeugen.
 */

export interface HeilungsErgebnis {
  einheitenAngelegt: number;
  raeumeZugeordnet: number;
  protokolleZugeordnet: number;
}

const jetzt = (): string => new Date().toISOString();

/** Standardeinheit eines Objekts — die ganze Fläche, keine echte Aufteilung. */
const standardEinheit = (objekt: {
  id: string;
  bezeichnung: string;
  lage: string | null;
  wohnflaeche: number | null;
  zimmeranzahl: number | null;
}): LokaleEinheit => ({
  id: standardEinheitId(objekt.id),
  objektId: objekt.id,
  bezeichnung: objekt.bezeichnung || EINHEIT_ART_LABEL.WOHNUNG,
  einheitNr: null,
  art: 'WOHNUNG',
  etage: null,
  lage: objekt.lage,
  wohnflaeche: objekt.wohnflaeche,
  zimmeranzahl: objekt.zimmeranzahl,
  reihenfolge: 0,
  notiz: null,
  aktiv: 1,
  schluessel: null,
  lokalGeaendertAm: jetzt(),
  synchronisiert: 0,
  geloescht: 0,
});

/**
 * Legt fehlende Standardeinheiten an und hängt verwaiste Räume ein.
 *
 * Läuft auch offline vollständig durch: Alles geht in IndexedDB und die
 * Outbox, der Versand passiert später. Mehrfaches Aufrufen ist harmlos — es
 * gibt nichts mehr zu heilen, sobald jedes Objekt eine Einheit hat.
 */
export const einheitenHeilen = async (): Promise<HeilungsErgebnis> => {
  const objekte = await db.objekte.toArray();
  if (objekte.length === 0) {
    return { einheitenAngelegt: 0, raeumeZugeordnet: 0, protokolleZugeordnet: 0 };
  }

  const einheiten = await db.einheiten.toArray();
  const objekteMitEinheit = new Set(einheiten.map((einheit) => einheit.objektId));

  const neue = objekte
    .filter((objekt) => objekt.geloescht === 0 && !objekteMitEinheit.has(objekt.id))
    .map(standardEinheit);

  if (neue.length > 0) {
    await db.einheiten.bulkPut(neue);
    for (const einheit of neue) {
      await inDieOutbox({
        entitaet: 'Einheit',
        operation: 'anlegen',
        entitaetId: einheit.id,
        gruppe: `Objekt:${einheit.objektId}`,
      });
    }
  }

  // Einheit je Objekt für die Zuordnung: die vorhandene, wenn es genau eine
  // gibt, sonst die gerade angelegte Standardeinheit. Bei mehreren echten
  // Einheiten bleibt die Zuordnung offen — dann kann nur ein Mensch
  // entscheiden, in welche Wohnung ein Raum gehört.
  const proObjekt = new Map<string, string>();
  for (const einheit of [...einheiten, ...neue]) {
    const bisher = proObjekt.get(einheit.objektId);
    if (bisher === undefined) proObjekt.set(einheit.objektId, einheit.id);
    else if (bisher !== einheit.id) proObjekt.set(einheit.objektId, '');
  }

  let raeumeZugeordnet = 0;
  const verwaisteRaeume = (await db.raeume.toArray()).filter(
    (raum) => !raum.einheitId && raum.geloescht === 0,
  );

  for (const raum of verwaisteRaeume) {
    const einheitId = proObjekt.get(raum.objektId);
    if (!einheitId) continue;

    await db.raeume.update(raum.id, {
      einheitId,
      lokalGeaendertAm: jetzt(),
      synchronisiert: 0,
    });
    await inDieOutbox({
      entitaet: 'Raum',
      operation: 'aendern',
      entitaetId: raum.id,
      gruppe: `Objekt:${raum.objektId}`,
    });
    raeumeZugeordnet += 1;
  }

  // Protokolle: nur Entwürfe. Ein unterschriebenes Protokoll wird nicht mehr
  // angefasst (CLAUDE.md §2) — es bleibt ohne Einheit und zeigt im PDF
  // weiterhin die Angaben des Objekts.
  let protokolleZugeordnet = 0;
  const offeneProtokolle = (await db.protokolle.toArray()).filter(
    (protokoll) =>
      !protokoll.einheitId &&
      protokoll.status === 'DRAFT' &&
      protokoll.konfliktkopie === 0 &&
      protokoll.geloescht === 0,
  );

  for (const protokoll of offeneProtokolle) {
    const einheitId = proObjekt.get(protokoll.objektId);
    if (!einheitId) continue;

    await db.protokolle.update(protokoll.id, {
      einheitId,
      revision: protokoll.revision + 1,
      lokalGeaendertAm: jetzt(),
      synchronisiert: 0,
    });
    await inDieOutbox({
      entitaet: 'Protocol',
      operation: protokoll.serverGeaendertAm ? 'aendern' : 'anlegen',
      entitaetId: protokoll.id,
      gruppe: `Protocol:${protokoll.id}`,
    });
    protokolleZugeordnet += 1;
  }

  return { einheitenAngelegt: neue.length, raeumeZugeordnet, protokolleZugeordnet };
};
